import { AxiosError } from 'axios'
import ApiClient from '../../utils/api/ApiClient'
import { ExportId } from './types'
import { PrescribedItemsDetail } from './types/exportApi'
import { downloadCsv, getExportFilename } from './utils/exportCsv'
import { isValidExportYyyymm, parseExportYyyymm } from './utils/exportMonth'

const PRESCRIBERS_EXPORT_PATH = '/bff/exports/prescribers'
const ORGANIZATION_ADMINS_EXPORT_PATH = '/bff/exports/organization-admins'
const PRESCRIBER_PRESCRIPTION_COUNTS_EXPORT_PATH = '/bff/exports/prescriber-prescription-counts'
const PRESCRIPTIONS_EXPORT_PATH = '/bff/exports/prescriptions'
const PRESCRIBED_ITEMS_EXPORT_PATH = '/bff/exports/prescribed-items'
const PRESCRIBERS_CSV_FILENAME = getExportFilename(ExportId.ACTIVE_PRESCRIBERS)
const ORGANIZATION_ADMINS_CSV_FILENAME = getExportFilename(ExportId.ACTIVE_ADMINS)

export class DataReportsExportError extends Error {
  statusCode?: number

  constructor(message: string, statusCode?: number) {
    super(message)
    this.name = 'DataReportsExportError'
    this.statusCode = statusCode
  }
}

async function getExportErrorResponseMessage(data: unknown): Promise<string | undefined> {
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { message?: unknown }

      return typeof parsed.message === 'string' ? parsed.message : undefined
    } catch {
      return undefined
    }
  }

  const message = (data as { message?: unknown } | undefined)?.message

  return typeof message === 'string' ? message : undefined
}

async function getDataReportsExportError(
  error: unknown,
  resourceLabel: string
): Promise<DataReportsExportError> {
  const axiosError = error as AxiosError<Blob | { message?: string }>
  const statusCode = axiosError.response?.status
  const serverMessage = await getExportErrorResponseMessage(axiosError.response?.data)

  if (statusCode === 403) {
    return new DataReportsExportError(
      `You do not have permission to export ${resourceLabel}.`,
      statusCode
    )
  }

  if (statusCode === 413) {
    return new DataReportsExportError(
      serverMessage || 'Export too large. Narrow the date range and try again.',
      statusCode
    )
  }

  const message = serverMessage || axiosError.message || `Failed to export ${resourceLabel}.`

  return new DataReportsExportError(message, statusCode)
}

async function downloadExportCsvFromBff(
  path: string,
  filename: string,
  params?: Record<string, string | number>
): Promise<string> {
  const response = await ApiClient.get<Blob>(path, {
    params: { format: 'csv', ...params },
    responseType: 'blob',
  })

  downloadCsv(filename, response.data)
  return filename
}

const downloadPrescribersExportCsv = async (): Promise<string> => {
  try {
    return await downloadExportCsvFromBff(PRESCRIBERS_EXPORT_PATH, PRESCRIBERS_CSV_FILENAME)
  } catch (error) {
    throw await getDataReportsExportError(error, 'prescribers')
  }
}

const downloadOrganizationAdminsExportCsv = async (): Promise<string> => {
  try {
    return await downloadExportCsvFromBff(
      ORGANIZATION_ADMINS_EXPORT_PATH,
      ORGANIZATION_ADMINS_CSV_FILENAME
    )
  } catch (error) {
    throw await getDataReportsExportError(error, 'organization admins')
  }
}

const downloadPrescriberPrescriptionCountsExportCsv = async (yyyymm: string): Promise<string> => {
  const monthParams = parseExportYyyymm(yyyymm)

  if (!monthParams || !isValidExportYyyymm(yyyymm)) {
    throw new DataReportsExportError('Select a valid month.')
  }

  try {
    return await downloadExportCsvFromBff(
      PRESCRIBER_PRESCRIPTION_COUNTS_EXPORT_PATH,
      getExportFilename(ExportId.PRESCRIPTIONS_BY_PRESCRIBER, yyyymm),
      { year: monthParams.year, month: monthParams.month }
    )
  } catch (error) {
    throw await getDataReportsExportError(error, 'prescriber prescription counts')
  }
}

const downloadPrescriptionsExportCsv = async (yyyymm: string): Promise<string> => {
  const monthParams = parseExportYyyymm(yyyymm)

  if (!monthParams || !isValidExportYyyymm(yyyymm)) {
    throw new DataReportsExportError('Select a valid month.')
  }

  try {
    return await downloadExportCsvFromBff(
      PRESCRIPTIONS_EXPORT_PATH,
      getExportFilename(ExportId.PRESCRIPTIONS, yyyymm),
      { year: monthParams.year, month: monthParams.month }
    )
  } catch (error) {
    throw await getDataReportsExportError(error, 'prescriptions')
  }
}

async function downloadPrescribedItemsExportCsvByDetail(
  yyyymm: string,
  detail: PrescribedItemsDetail,
  exportId: ExportId,
  resourceLabel: string
): Promise<string> {
  const monthParams = parseExportYyyymm(yyyymm)

  if (!monthParams || !isValidExportYyyymm(yyyymm)) {
    throw new DataReportsExportError('Select a valid month.')
  }

  try {
    return await downloadExportCsvFromBff(
      PRESCRIBED_ITEMS_EXPORT_PATH,
      getExportFilename(exportId, yyyymm),
      { year: monthParams.year, month: monthParams.month, detail }
    )
  } catch (error) {
    throw await getDataReportsExportError(error, resourceLabel)
  }
}

const downloadPrescribedItemsExportCsv = async (yyyymm: string): Promise<string> =>
  downloadPrescribedItemsExportCsvByDetail(
    yyyymm,
    'full',
    ExportId.PRESCRIBED_ITEMS,
    'prescribed items'
  )

const downloadLitePrescribedItemsExportCsv = async (yyyymm: string): Promise<string> =>
  downloadPrescribedItemsExportCsvByDetail(
    yyyymm,
    'lite',
    ExportId.LITE_PRESCRIBED_ITEMS,
    'lite prescribed items'
  )

export const DataReportsClient = {
  downloadPrescribersExportCsv,
  downloadOrganizationAdminsExportCsv,
  downloadPrescriberPrescriptionCountsExportCsv,
  downloadPrescriptionsExportCsv,
  downloadPrescribedItemsExportCsv,
  downloadLitePrescribedItemsExportCsv,
}
